import React from 'react'
import { Collapsible } from './ui/Collapsible'
import { FontPicker, MonoFontPicker } from './ui/FontPicker'
import { ColorPicker } from './ui/ColorPicker'
import {
  DesignTokens,
  TailwindColor,
  NeutralColor,
  BorderRadius,
  Shadow,
  MotifId,
} from '../types'
import { TAILWIND_COLORS, NEUTRAL_COLORS, COLOR_VALUES } from '../data/tailwindColors'
import { MOTIFS } from '../data/motifs'
import { DISPLAY_FONT_SIZES, BODY_FONT_SIZES } from '../data/googleFonts'

interface SidebarProps {
  tokens: DesignTokens
  systemName: string
  onUpdateToken: <K extends keyof DesignTokens>(key: K, value: DesignTokens[K]) => void
  onUpdateTypography: <K extends keyof DesignTokens['typography']>(
    key: K,
    value: DesignTokens['typography'][K]
  ) => void
  onResetToMotif: (motifId: MotifId) => void
  onRenameSystem: (name: string) => void
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export function Sidebar({
  tokens,
  systemName,
  onUpdateToken,
  onUpdateTypography,
  onResetToMotif,
  onRenameSystem,
}: SidebarProps) {
  return (
    <aside className="w-80 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden">
      {/* System Name Input */}
      <div className="px-4 py-3 border-b border-gray-200">
        <input
          type="text"
          value={systemName}
          onChange={(e) => onRenameSystem(e.target.value)}
          placeholder="System name"
          className="w-full px-3 py-2 text-sm font-medium border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />
      </div>

      {/* Scrollable Sections */}
      <div className="flex-1 overflow-y-auto">
        {/* Presets Section - At the top, collapsible */}
        <Collapsible title="Presets" defaultOpen>
          <div className="grid grid-cols-2 gap-2">
            {MOTIFS.map((motif) => {
              const isActive =
                tokens.primaryColor === motif.tokens.primaryColor &&
                tokens.typography.displayFont.family === motif.tokens.typography.displayFont.family

              return (
                <button
                  key={motif.id}
                  onClick={() => onResetToMotif(motif.id)}
                  className={`
                    flex items-center gap-2 p-2 text-left text-xs border rounded-lg transition-all cursor-pointer
                    ${isActive
                      ? 'border-gray-900 bg-gray-900 text-white shadow-md ring-2 ring-gray-900 ring-offset-2'
                      : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-700'
                    }
                  `}
                  title={motif.description}
                >
                  <div
                    className={`w-4 h-4 rounded-sm flex-shrink-0 ${isActive ? 'ring-1 ring-white/50' : ''}`}
                    style={{ backgroundColor: COLOR_VALUES[motif.tokens.primaryColor] }}
                  />
                  <span className="truncate font-medium">{motif.name}</span>
                </button>
              )
            })}
          </div>
        </Collapsible>

        {/* Colors Section - Compact row with labels */}
        <Collapsible title="Colors" defaultOpen>
          <div className="flex items-start gap-4">
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs text-gray-500">Primary</span>
              <ColorPicker
                label="Primary"
                value={tokens.primaryColor}
                options={TAILWIND_COLORS}
                onChange={(color) => color && onUpdateToken('primaryColor', color as TailwindColor)}
                compact
              />
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs text-gray-500">Secondary</span>
              <ColorPicker
                label="Secondary"
                value={tokens.secondaryColor}
                options={TAILWIND_COLORS}
                onChange={(color) => onUpdateToken('secondaryColor', color as TailwindColor | null)}
                allowNull
                compact
              />
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs text-gray-500">Neutral</span>
              <ColorPicker
                label="Neutral"
                value={tokens.neutralColor}
                options={NEUTRAL_COLORS}
                onChange={(color) => color && onUpdateToken('neutralColor', color as NeutralColor)}
                compact
              />
            </div>
          </div>
        </Collapsible>

        {/* Typography Section */}
        <Collapsible title="Typography" defaultOpen>
          <div className="space-y-4">
            <FontPicker
              label="Display Font"
              family={tokens.typography.displayFont.family}
              weight={tokens.typography.displayFont.weight}
              size={tokens.typography.displayFont.size}
              onFamilyChange={(family) =>
                onUpdateTypography('displayFont', {
                  ...tokens.typography.displayFont,
                  family,
                })
              }
              onWeightChange={(weight) =>
                onUpdateTypography('displayFont', {
                  ...tokens.typography.displayFont,
                  weight,
                })
              }
              onSizeChange={(size) =>
                onUpdateTypography('displayFont', {
                  ...tokens.typography.displayFont,
                  size,
                })
              }
              fontFilter={(f) => f.category !== 'monospace'}
              sizeOptions={DISPLAY_FONT_SIZES}
            />

            <FontPicker
              label="Body Font"
              family={tokens.typography.bodyFont.family}
              weight={tokens.typography.bodyFont.weight}
              size={tokens.typography.bodyFont.size}
              onFamilyChange={(family) =>
                onUpdateTypography('bodyFont', {
                  ...tokens.typography.bodyFont,
                  family,
                })
              }
              onWeightChange={(weight) =>
                onUpdateTypography('bodyFont', {
                  ...tokens.typography.bodyFont,
                  weight,
                })
              }
              onSizeChange={(size) =>
                onUpdateTypography('bodyFont', {
                  ...tokens.typography.bodyFont,
                  size,
                })
              }
              fontFilter={(f) => f.category === 'sans-serif' || f.category === 'serif'}
              sizeOptions={BODY_FONT_SIZES}
            />

            <MonoFontPicker
              label="Monospace Font"
              family={tokens.typography.monoFont.family}
              onFamilyChange={(family) =>
                onUpdateTypography('monoFont', { family })
              }
            />
          </div>
        </Collapsible>

        {/* Layout Section */}
        <Collapsible title="Layout" defaultOpen>
          <div className="space-y-3">
            <SelectField<BorderRadius>
              label="Border Radius"
              value={tokens.borderRadius}
              options={[
                { value: 'none', label: 'None' },
                { value: 'sm', label: 'Small' },
                { value: 'md', label: 'Medium' },
                { value: 'lg', label: 'Large' },
                { value: 'xl', label: 'Extra Large' },
                { value: 'full', label: 'Full' },
              ]}
              onChange={(value) => onUpdateToken('borderRadius', value)}
            />

            <SelectField<Shadow>
              label="Shadow"
              value={tokens.shadow}
              options={[
                { value: 'none', label: 'None' },
                { value: 'sm', label: 'Small' },
                { value: 'md', label: 'Medium' },
                { value: 'lg', label: 'Large' },
              ]}
              onChange={(value) => onUpdateToken('shadow', value)}
            />
          </div>
        </Collapsible>
      </div>
    </aside>
  )
}
